import { useState, useCallback } from 'react'
import { useDevToolsStore, type NetworkEntry } from '../store/devtools-store'
import { JsonTree } from './json-tree'

type MethodFilter = 'all' | 'GET' | 'POST' | 'PATCH' | 'DELETE'
type StatusFilter = 'all' | '2xx' | '4xx' | '5xx'

function formatTime(ts: number): string {
  const d = new Date(ts)
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    + '.' + String(d.getMilliseconds()).padStart(3, '0')
}

function methodColor(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET': return 'bg-blue-500/10 text-blue-400'
    case 'POST': return 'bg-primary/10 text-primary'
    case 'PATCH': return 'bg-yellow-500/10 text-yellow-400'
    case 'PUT': return 'bg-orange-500/10 text-orange-400'
    case 'DELETE': return 'bg-red-500/10 text-red-400'
    default: return 'bg-muted text-muted-foreground'
  }
}

function statusColor(status: number): string {
  if (status >= 200 && status < 300) return 'text-success'
  if (status >= 300 && status < 400) return 'text-yellow-400'
  return 'text-red-400'
}

function durationColor(ms: number): string {
  if (ms < 200) return 'text-success'
  if (ms < 500) return 'text-yellow-400'
  return 'text-red-400'
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url)
    // Same-origin: just show path
    if (u.origin === window.location.origin) return u.pathname + u.search
    return u.pathname + u.search
  } catch {
    return url
  }
}

function statusInRange(status: number, filter: StatusFilter): boolean {
  if (filter === 'all') return true
  if (filter === '2xx') return status >= 200 && status < 300
  if (filter === '4xx') return status >= 400 && status < 500
  if (filter === '5xx') return status >= 500
  return true
}

function NetworkRow({ entry }: { entry: NetworkEntry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border-b border-border/50">
      <div
        className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/30 cursor-pointer text-xs"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-muted-foreground font-mono shrink-0 w-20">{formatTime(entry.timestamp)}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase shrink-0 w-14 text-center ${methodColor(entry.method)}`}>
          {entry.method}
        </span>
        <span className="text-foreground/80 font-mono truncate flex-1" title={entry.url}>
          {truncateUrl(entry.url)}
        </span>
        <span className={`font-mono shrink-0 w-8 text-right ${statusColor(entry.status)}`}>
          {entry.status}
        </span>
        <span className={`font-mono shrink-0 w-16 text-right ${durationColor(entry.duration)}`}>
          {entry.duration}ms
        </span>
        <span className="text-muted-foreground shrink-0 w-3">{expanded ? '\u25BC' : '\u25B6'}</span>
      </div>
      {expanded && (
        <div className="px-3 py-2 bg-muted/10 border-t border-border/30 space-y-2">
          {entry.error && (
            <div className="text-xs text-red-400 font-mono">{entry.error}</div>
          )}
          {entry.requestBody != null && (
            <div>
              <div className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">Request Body</div>
              <JsonTree data={entry.requestBody} defaultExpanded />
            </div>
          )}
          {entry.responseBody != null && (
            <div>
              <div className="text-[10px] text-muted-foreground uppercase font-semibold mb-1">Response Body</div>
              <JsonTree data={entry.responseBody} defaultExpanded />
            </div>
          )}
          {!entry.requestBody && !entry.responseBody && !entry.error && (
            <div className="text-xs text-muted-foreground">No body data captured.</div>
          )}
        </div>
      )}
    </div>
  )
}

export function NetworkTab() {
  const networkLog = useDevToolsStore((s) => s.networkLog)
  const clearNetworkLog = useDevToolsStore((s) => s.clearNetworkLog)

  const [methodFilter, setMethodFilter] = useState<MethodFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')

  const filtered = networkLog.filter((entry) => {
    if (methodFilter !== 'all' && entry.method.toUpperCase() !== methodFilter) return false
    if (!statusInRange(entry.status, statusFilter)) return false
    return true
  })

  const reversed = [...filtered].reverse()

  const toggleMethod = useCallback((m: MethodFilter) => {
    setMethodFilter((prev) => prev === m ? 'all' : m)
  }, [])

  const toggleStatus = useCallback((s: StatusFilter) => {
    setStatusFilter((prev) => prev === s ? 'all' : s)
  }, [])

  const methodButtons: MethodFilter[] = ['GET', 'POST']
  const statusButtons: StatusFilter[] = ['2xx', '4xx', '5xx']

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <span className="text-xs text-muted-foreground">{networkLog.length} requests</span>
        <div className="flex-1" />
        {/* Method filters */}
        {methodButtons.map((m) => (
          <button
            key={m}
            onClick={() => toggleMethod(m)}
            className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
              methodFilter === m
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {m}
          </button>
        ))}
        <span className="text-border">|</span>
        {/* Status filters */}
        {statusButtons.map((s) => (
          <button
            key={s}
            onClick={() => toggleStatus(s)}
            className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
              statusFilter === s
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {s}
          </button>
        ))}
        <span className="text-border">|</span>
        <button
          onClick={clearNetworkLog}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      </div>

      {/* Request list */}
      <div className="flex-1 overflow-auto">
        {networkLog.length === 0 && (
          <p className="text-xs text-muted-foreground px-3 py-4 text-center">
            No network requests captured. API calls from the app preview will appear here.
          </p>
        )}
        {networkLog.length > 0 && reversed.length === 0 && (
          <p className="text-xs text-muted-foreground px-3 py-4 text-center">
            No requests match the current filters.
          </p>
        )}
        {reversed.map((entry) => (
          <NetworkRow key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  )
}
